(function () {
  let popup = null;

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function shopifyImgUrl(src, size) {
    if (!src) return '';
    return src.replace(
      /(\.(jpg|jpeg|gif|png|bmp|webp))(\?.*)?$/i,
      `_${size}$1$3`
    );
  }

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'INR';
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
      }).format(cents / 100);
    } catch (_) {
      return '₹' + (cents / 100).toFixed(2);
    }
  }

  // ─── Popup DOM ────────────────────────────────────────────────────────────

  function buildPopup() {
    const el = document.createElement('div');
    el.id = 'PwQuickView';
    el.className = 'pw-qv';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-labelledby', 'PwQvTitle');
    el.innerHTML = `
      <div class="pw-qv__overlay" data-pw-qv-close></div>
      <div class="pw-qv__modal">
        <button type="button" class="pw-qv__close button-reset" data-pw-qv-close aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="pw-qv__image-wrap">
          <img class="pw-qv__image" src="" alt="" loading="lazy">
        </div>
        <div class="pw-qv__body">
          <h3 class="pw-qv__title" id="PwQvTitle"></h3>
          <div class="pw-qv__price"></div>
          <p class="pw-qv__size-label">Select size</p>
          <ul class="pw-qv__sizes list-unstyled"></ul>
          <div class="pw-qv__atc-wrap">
            <button type="button" class="pw-qv__atc-btn" data-pw-qv-atc disabled>Add to Bag</button>
          </div>
          <div class="pw-qv__messages hidden">
            <p class="pw-qv__msg pw-qv__msg--success hidden">Added to bag!</p>
            <p class="pw-qv__msg pw-qv__msg--error hidden"></p>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(el);

    // Close on overlay / close-btn click
    el.addEventListener('click', e => {
      if (e.target.closest('[data-pw-qv-close]')) closePopup();
    });

    // Close on Escape
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && el.classList.contains('is-open')) closePopup();
    });

    // Size button selection
    el.addEventListener('click', e => {
      const sizeBtn = e.target.closest('.pw-qv__size-btn');
      if (!sizeBtn || sizeBtn.classList.contains('is-unavailable')) return;

      el.querySelectorAll('.pw-qv__size-btn').forEach(b => b.classList.remove('is-selected'));
      sizeBtn.classList.add('is-selected');

      const atcBtn = el.querySelector('[data-pw-qv-atc]');
      atcBtn.dataset.variantid = sizeBtn.dataset.variantid;
      atcBtn.removeAttribute('disabled');
    });

    // Add-to-cart button
    el.addEventListener('click', e => {
      const atcBtn = e.target.closest('[data-pw-qv-atc]');
      if (!atcBtn || atcBtn.hasAttribute('disabled')) return;
      addToCart(atcBtn.dataset.variantid);
    });

    return el;
  }

  function openPopup() {
    if (!popup) popup = buildPopup();
    popup.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    popup.querySelector('.pw-qv__modal').focus();
  }

  function closePopup() {
    popup?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  // ─── Render product into popup ────────────────────────────────────────────

  function renderProduct(product, sizesOptionName) {
    const img       = popup.querySelector('.pw-qv__image');
    const titleEl   = popup.querySelector('.pw-qv__title');
    const priceEl   = popup.querySelector('.pw-qv__price');
    const sizeLabel = popup.querySelector('.pw-qv__size-label');
    const sizesList = popup.querySelector('.pw-qv__sizes');
    const atcBtn    = popup.querySelector('[data-pw-qv-atc]');
    const messages  = popup.querySelector('.pw-qv__messages');

    // Reset
    messages.classList.add('hidden');
    messages.querySelectorAll('.pw-qv__msg').forEach(m => {
      m.classList.add('hidden');
      m.textContent = '';
    });
    atcBtn.setAttribute('disabled', '');
    delete atcBtn.dataset.variantid;
    sizesList.querySelectorAll('.pw-qv__size-btn').forEach(b => b.classList.remove('is-selected'));

    // Image — product.js can return featured_image as a string or object
    const rawImg = typeof product.featured_image === 'string'
      ? product.featured_image
      : (product.featured_image?.src || '');
    img.src = shopifyImgUrl(rawImg, '400x');
    img.alt = product.title;

    // Title
    titleEl.textContent = product.title;

    // Price — use cheapest available variant
    const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
    const priceHtml = firstAvailable
      ? `<span class="pw-qv__price-current">${formatMoney(firstAvailable.price)}</span>`
        + (firstAvailable.compare_at_price > firstAvailable.price
          ? ` <span class="pw-qv__price-compare">${formatMoney(firstAvailable.compare_at_price)}</span>`
          : '')
      : '';
    priceEl.innerHTML = priceHtml;

    // Determine if product has a size option
    const optionIdx = product.options
      .map(o => (typeof o === 'string' ? o : o.name)?.toLowerCase())
      .indexOf((sizesOptionName || 'size').toLowerCase());

    const hasSizes = optionIdx !== -1 && product.variants.length > 1;

    if (!hasSizes) {
      // Single / no-size product — hide picker, pre-set variant on ATC
      sizeLabel.classList.add('hidden');
      sizesList.innerHTML = '';
      atcBtn.dataset.variantid = product.variants[0].id;
      atcBtn.removeAttribute('disabled');
    } else {
      sizeLabel.classList.remove('hidden');
      const optKey = `option${optionIdx + 1}`; // option1 / option2 / option3
      const seen = new Set();

      sizesList.innerHTML = product.variants
        .filter(v => {
          const val = v[optKey];
          if (seen.has(val)) return false;
          seen.add(val);
          return true;
        })
        .map(v => {
          const val = v[optKey];
          const unavail = !v.available;
          return `<li>
            <button
              type="button"
              class="pw-qv__size-btn${unavail ? ' is-unavailable' : ''}"
              data-variantid="${v.id}"
              data-size="${val}"
              ${unavail ? 'disabled' : ''}
            >${val}</button>
          </li>`;
        })
        .join('');
    }
  }

  // ─── Add to cart ──────────────────────────────────────────────────────────

  async function addToCart(variantId) {
    if (!variantId) return;

    const atcBtn   = popup.querySelector('[data-pw-qv-atc]');
    const messages = popup.querySelector('.pw-qv__messages');
    const successMsg = popup.querySelector('.pw-qv__msg--success');
    const errorMsg   = popup.querySelector('.pw-qv__msg--error');

    atcBtn.setAttribute('disabled', '');
    // atcBtn.textContent = '...';

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      if (cartDrawer?.getSectionsToRender) {
        formData.append(
          'sections',
          cartDrawer.getSectionsToRender().map(s => s.section)
        );
        formData.append('sections_url', window.location.pathname);
      }

      // Mirror card-product.js exactly: fetchConfig sets Accept + Content-Type,
      // then we delete Content-Type so FormData can set its own boundary.
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const res = await fetch(window.routes.cart_add_url, config);
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Error adding to cart');

      if (cartDrawer?.renderContents) cartDrawer.renderContents(data, false);

      successMsg.textContent = 'Added to bag!';
      successMsg.classList.remove('hidden');
      errorMsg.classList.add('hidden');
      messages.classList.remove('hidden');

      setTimeout(closePopup, 1400);

    } catch (err) {
      errorMsg.textContent = err.message || 'Something went wrong';
      errorMsg.classList.remove('hidden');
      successMsg.classList.add('hidden');
      messages.classList.remove('hidden');

      atcBtn.removeAttribute('disabled');
      atcBtn.textContent = 'Add to Bag';
    }
  }

  // ─── Trigger ──────────────────────────────────────────────────────────────

  document.addEventListener('click', async e => {
    const btn = e.target.closest('.js-pw-quick-view-btn');
    if (!btn) return;

    const handle      = btn.dataset.handle;
    const sizesOption = btn.dataset.sizesOption;
    if (!handle) return;

    if (!popup) popup = buildPopup();

    // Show skeleton immediately
    popup.querySelector('.pw-qv__title').textContent = '';
    popup.querySelector('.pw-qv__price').innerHTML = '';
    popup.querySelector('.pw-qv__sizes').innerHTML =
      '<li class="pw-qv__loading">Loading&hellip;</li>';
    popup.querySelector('[data-pw-qv-atc]').setAttribute('disabled', '');
    popup.querySelector('.pw-qv__image').src = '';
    openPopup();

    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}products/${handle}.js`);
      if (!res.ok) throw new Error(`Product fetch failed: ${res.status}`);
      const product = await res.json();
      renderProduct(product, sizesOption);
    } catch (err) {
      console.error('[PairWithQuickView]', err);
      popup.querySelector('.pw-qv__sizes').innerHTML =
        `<li class="pw-qv__loading">${err.message || 'Failed to load product.'}</li>`;
    }
  });
})();
